import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Lightning, ShieldCheck, CheckCircle, Handshake } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import InterestModal from "@/components/InterestModal";
import { useSiteSettings } from "@/context/SiteSettingsContext";

/**
 * /event/:code — public landing page for an event-exclusive consultation offer.
 *
 * Reusable across events: change the code (e.g. /event/hl1may, /event/hl2nov,
 * /event/expo26) and the page picks up the matching record from /api/promotions/by-code/:code
 * if it exists, otherwise falls back to a sensible generic event copy.
 *
 * The code is also persisted to localStorage so the matching promo banner stays
 * unlocked while the visitor browses the rest of the site.
 */
export default function EventLanding() {
  const { settings } = useSiteSettings();
  const { code = "" } = useParams();
  const [promo, setPromo] = useState(null);
  const [eventDoc, setEventDoc] = useState(null);
  const [interestOpen, setInterestOpen] = useState(false);

  useEffect(() => {
    if (!code) return;
    try { localStorage.setItem("appistry.promo", code); } catch (_) {}
    // Look up event-code record (admin-managed). Falls back to defaults if not found.
    api.get(`/event-codes/${encodeURIComponent(code)}`)
      .then(({ data }) => setEventDoc(data))
      .catch(() => setEventDoc(null));
    // Best-effort: pull matching promo banner record (legacy)
    api.get("/promotions/active").then(({ data }) => {
      if (!data) return;
      const rule = data.display_rule || "";
      if (rule === `code:${code}`) setPromo(data);
    }).catch(() => {});
  }, [code]);

  const minutes = eventDoc?.consultation_minutes || 30;
  const eventName = eventDoc?.event_label || promo?.event_label || (code === "hl1may" ? "the May 1st networking event" : "our recent event");
  const offerHeadline = eventDoc?.headline || promo?.title?.replace(/^Event-exclusive:\s*/i, "") || `Free ${minutes}-min Practical Build consultation`;
  const offerBody = eventDoc?.blurb || promo?.description || `A thank-you for stopping by. Bring one friction in your day. We'll spend ${minutes} minutes naming the smallest thing that would help — no slide decks, no hard sell.`;

  return (
    <div data-testid="event-landing">
      {/* HERO */}
      <section className="relative overflow-hidden bg-zinc-950 text-white">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle, #FFFFFF 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
        <div className="absolute right-[-10%] top-[-20%] h-[600px] w-[600px] rounded-full opacity-30 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--appistry-signal)) 0%, transparent 70%)" }} />
        <div className="app-container relative py-20 md:py-28">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 bg-[hsl(var(--appistry-signal))]" data-testid="event-badge">
            <Lightning size={14} weight="fill" />
            <span className="text-[10px] font-mono uppercase tracking-[2.5px] font-bold">EVENT SPECIAL · CODE {code.toUpperCase()}</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6 max-w-4xl" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="event-headline">
            {offerHeadline}.
          </h1>
          <p className="text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed mb-10">
            {offerBody}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={`/consultation?promo=${encodeURIComponent(code)}&source=event_${encodeURIComponent(code)}`} className="btn-signal" data-testid="event-claim-cta">
              Claim my free {minutes} minutes <ArrowRight size={18} weight="bold" />
            </Link>
            <button onClick={() => setInterestOpen(true)} className="btn-ghost-ink !text-white !border-white/30 hover:!bg-white/10" data-testid="event-interest-cta">
              Or just register interest
            </button>
          </div>
          <p className="mt-6 text-xs font-mono uppercase tracking-[2.5px] text-zinc-500">
            VALID FOR ATTENDEES OF {eventName.toUpperCase()}
          </p>
        </div>
      </section>

      {/* WHAT YOU'LL WALK AWAY WITH */}
      <section className="border-b border-zinc-200">
        <div className="app-container py-20 md:py-28">
          <p className="overline mb-4">What's in it</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight max-w-3xl mb-14" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {minutes} minutes. Three real outcomes. No theatre.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-zinc-200">
            {[
              { i: "01", title: "The friction, named", body: "We help you put words on the bit of your week that quietly costs the most — the one a small app could lift." },
              { i: "02", title: "A shippable slice", body: "We pick the smallest thing that would actually help and sketch how it would look. No bloated discovery doc." },
              { i: "03", title: "An honest answer", body: "We tell you whether we're the right people to build it. Sometimes the answer is no. That's still useful." },
            ].map((s) => (
              <div key={s.i} className="p-8 md:p-10 border-r border-b border-zinc-200 bg-white" data-testid={`event-pillar-${s.i}`}>
                <p className="overline text-zinc-500 mb-4">{s.i}</p>
                <h3 className="text-xl font-semibold tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h3>
                <p className="text-zinc-600 leading-relaxed text-sm">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="app-container py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <p className="overline mb-4">Who we are</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Niche software, built like it matters.
              </h2>
              <p className="text-zinc-600 leading-relaxed mb-4">
                We co-build the unglamorous apps off-the-shelf SaaS has ignored — for therapists, home bakers, residents&rsquo; committees, sole traders, and the small businesses in between.
              </p>
              <p className="text-zinc-600 leading-relaxed">
                One live product. A stable of niche concepts open for founding partners. And a relationship that doesn't end at handover — we host, support, and keep innovating with you.
              </p>
            </div>
            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/solutions" className="block p-6 border border-zinc-200 hover:border-zinc-950 transition-colors group" data-testid="event-explore-solutions">
                <p className="overline text-zinc-500 mb-3">For real</p>
                <h4 className="text-lg font-semibold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Live solutions</h4>
                <p className="text-sm text-zinc-600 mb-4">Apps already in production for residents, sport clubs, and software teams.</p>
                <span className="text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">See solutions <ArrowRight size={14} weight="bold" /></span>
              </Link>
              {settings.co_innovation_enabled && (
                <Link to="/co-innovation" className="block p-6 border border-zinc-200 hover:border-zinc-950 transition-colors group" data-testid="event-explore-coinnovation">
                  <p className="overline text-zinc-500 mb-3">Founding partners wanted</p>
                  <h4 className="text-lg font-semibold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Co-innovation</h4>
                  <p className="text-sm text-zinc-600 mb-4">3 places this half. Bring a problem, we co-build it, you keep using it.</p>
                  <span className="text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">See the programme <ArrowRight size={14} weight="bold" /></span>
                </Link>
              )}
              <Link to="/demos" className="block p-6 border border-zinc-200 hover:border-zinc-950 transition-colors group" data-testid="event-explore-demos">
                <p className="overline text-zinc-500 mb-3">Concept lab</p>
                <h4 className="text-lg font-semibold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Poke at concepts</h4>
                <p className="text-sm text-zinc-600 mb-4">11 niche concepts. Click through, try them out, see if anything sparks.</p>
                <span className="text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Browse concepts <ArrowRight size={14} weight="bold" /></span>
              </Link>
              <Link to="/card" className="block p-6 border border-zinc-200 bg-zinc-950 text-white hover:bg-zinc-800 transition-colors group" data-testid="event-explore-card">
                <p className="overline text-zinc-500 mb-3">Save us</p>
                <h4 className="text-lg font-semibold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Digital business card</h4>
                <p className="text-sm text-zinc-400 mb-4">One tap to save us to your contacts. Email, links, the lot.</p>
                <span className="text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Open card <ArrowRight size={14} weight="bold" /></span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-zinc-50">
        <div className="app-container py-20 md:py-24 text-center">
          <Handshake size={36} weight="fill" className="text-[hsl(var(--appistry-signal))] mx-auto mb-5" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ready to claim your {minutes} minutes?
          </h2>
          <p className="text-zinc-600 max-w-xl mx-auto mb-8">
            Drop us a few details and your preferred times. We'll come back to you with a slot that suits.
          </p>
          <Link to={`/consultation?promo=${encodeURIComponent(code)}&source=event_${encodeURIComponent(code)}`} className="btn-signal inline-flex" data-testid="event-final-cta">
            Claim my free {minutes} minutes <ArrowRight size={16} weight="bold" />
          </Link>
          <p className="mt-6 text-xs text-zinc-500 inline-flex items-center gap-1.5">
            <ShieldCheck size={12} weight="fill" /> No spam. UK GDPR compliant. Unsubscribe anytime.
          </p>
        </div>
      </section>

      <InterestModal
        open={interestOpen}
        onClose={() => setInterestOpen(false)}
        source={`event_${code}`}
        title={`Stay in the loop · ${eventName}`}
        body="Drop your email — we'll let you know when new concepts go live, and keep your free-consultation offer warm for next time we chat."
        ctaLabel="Register interest"
      />
    </div>
  );
}
